import React, { useState, useEffect } from "react";
import { api } from "../shared/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import { postcodeValidator } from "postcode-validator";
import countries from "i18n-iso-countries";

// Register the English locale for country validation
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const CandidateSignUp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const referredBy = params.get("referredBy") || "";
    const jobId = params.get("jobId") || "";

    // If a logged-in candidate clicks a job link, apply them to the job and redirect
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token && jobId) {
            api.post("/matches/apply", { jobId })
                .then(() => navigate("/matches", { state: { appliedJobId: jobId } }))
                .catch(() => navigate("/matches"));
        }
    }, [jobId, navigate]);

    const [formData, setFormData] = useState({
        email: "",
        firstName: "",
        lastName: "",
        postalCode: "",
        country: "",
        linkedinProfile: "",
        password: "",
        careerSatisfaction: "",
        referredBy: referredBy,
        jobId: jobId,
        requiresSponsorship: false
    });

    const [error, setError] = useState("");
    const [, setLoading] = useState(false);

    const getLabel = (fieldName) => {
        switch (fieldName) {
            case "postalCode":
                return "Postal Code";
            case "country":
                return "Country (e.g. US, MX)";
            case "linkedinProfile":
                return "LinkedIn Profile";
            default:
                // Insert a space before capital letters and capitalize the first letter
                return fieldName
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase());
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Only allow letters for the country field
        if (name === "country") {
            const filteredValue = value.replace(/[^A-Za-z]/g, "");
            setFormData({ ...formData, [name]: filteredValue });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        // Validate the two-letter country code (ISO 3166-1 alpha-2)
        const countryCode = formData.country.trim().toUpperCase();
        if (!countries.isValid(countryCode)) {
            setError("Please enter a valid two-letter country code (e.g. US, MX, CA).");
            setLoading(false);
            return;
        }

        // Validate the postal code for the given country
        if (!postcodeValidator(formData.postalCode, countryCode)) {
            setError("Please enter a valid postal code for the given country.");
            setLoading(false);
            return;
        }

        try {
            await api.post("/auth/register-candidate", formData);
            navigate("/signin");
        } catch (err) {
            setError(err.response?.data?.error || "An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };
// Fields to display in the form
    const fields = [
        "email",
        "firstName",
        "lastName",
        "postalCode",
        "country",
        "linkedinProfile",
        "password",
    ];
    return (
        <div className="min-h-screen flex items-center justify-center bg-darkblue text-lightgray relative">
            <div className="w-full max-w-md bg-bluegray shadow-lg rounded-lg p-6">
                <h1 className="text-3xl font-bold text-center text-orange mb-6">Join the Mission</h1>
                {error && <div className="bg-red text-white text-sm p-2 rounded mb-4">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {fields.map((field) => {
                        const label = getLabel(field);
                        return (
                            <div key={field}>
                                <label htmlFor={field} className="block text-sm font-medium mb-1">
                                    {label}
                                </label>
                                {field === "password" && (
                                    <p className="text-xs text-gray-400 mb-1">
                                        Minimum 12 characters. <a href="https://xkcd.com/936" target="_blank" rel="noopener noreferrer" className="text-lightblue underline">Tips</a>
                                    </p>
                                )}
                                <input
                                    type={field === "password" ? "password" : "text"}
                                    id={field}
                                    name={field}
                                    value={formData[field]}
                                    onChange={handleChange}
                                    placeholder={field === "password" ? "Min 12 characters" : `Enter your ${label}`}
                                    required
                                    minLength={field === "password" ? 12 : undefined}
                                    autoComplete={
                                        field === "email"
                                            ? "email"
                                            : field === "password"
                                                ? "new-password"
                                                : field === "firstName"
                                                    ? "given-name"
                                                    : field === "lastName"
                                                        ? "family-name"
                                                        : field === "postalCode"
                                                            ? "postal-code"
                                                            : field === "country"
                                                                ? "country"
                                                                : "off"
                                    }
                                    // Limit the country field to 2 characters
                                    maxLength={field === "country" ? 2 : undefined}
                                    className="w-full px-3 py-2 rounded bg-deepblue text-lightgray border border-lightblue focus:outline-none focus:ring-2 focus:ring-orange"
                                />
                            </div>
                        );
                    })}
                    <div>
                        <label htmlFor="careerSatisfaction" className="block text-sm font-medium mb-1">
                            On a scale of 1-10, how satisfied are you with your career today? (Employers don't see this)
                        </label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((number) => (
                                <label key={number} className="flex items-center space-x-1 cursor-pointer">
                                    <input
                                        type="radio"
                                        id={`careerSatisfaction-${number}`}
                                        name="careerSatisfaction"
                                        required={true}
                                        value={number}
                                        checked={formData.careerSatisfaction === number.toString()}
                                        onChange={handleChange}
                                        className="form-radio h-4 w-4 text-orange"
                                    />
                                    <span>{number}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center mt-2">
                            <input
                                type="checkbox"
                                id="requiresSponsorship"
                                name="requiresSponsorship"
                                checked={formData.requiresSponsorship}
                                onChange={(e) => setFormData({ ...formData, requiresSponsorship: e.target.checked })}
                                className="mr-2 h-4 w-4"
                            />
                            <label htmlFor="requiresSponsorship" className="text-sm font-medium">
                                I require visa sponsorship to work in this country
                            </label>
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="w-full py-2 px-4 bg-orange text-white font-bold rounded hover:bg-deeporange"
                    >
                        Sign Up
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CandidateSignUp;
